import { useMemo, useState } from "react";
import { ActionList } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import SortIcon from "../../components/ui/Sorting";
import SortSummary from "../../components/ui/SortSummary";
import { formatDateTime } from "../../helpers/formatDateTime";
import type { ScheduleSummary } from "../../services/configuration/configuration.types";

const COLUMNS = [
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    { key: "timeSlots", label: "Time Slots" },
    { key: "createdAt", label: "Created" },
] as const;
type SortColumn = typeof COLUMNS[number]["key"];

interface Props {
    schedules: ScheduleSummary[];
    loading: boolean;
    busy: boolean;
    editorOpen: boolean;
    onEdit: (schedule: ScheduleSummary) => void;
    onDelete: (schedule: ScheduleSummary) => void;
}

export function ScheduleList({ schedules, loading, busy, editorOpen, onEdit, onDelete }: Props) {
    const [sortColumn, setSortColumn] = useState<SortColumn>("createdAt");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [hasAppliedSort, setHasAppliedSort] = useState(false);
    const activeColumn = COLUMNS.find(column => column.key === sortColumn);
    const sorted = useMemo(() => {
        const value = (schedule: ScheduleSummary) => {
            switch (sortColumn) {
                case "name": return schedule.name.toLowerCase();
                case "description": return (schedule.description ?? "").toLowerCase();
                case "timeSlots": return schedule.timeSlotCount ?? 0;
                case "createdAt": return schedule.createdAt ? new Date(schedule.createdAt).getTime() : 0;
            }
        };
        return [...schedules].sort((left, right) => {
            const first = value(left);
            const second = value(right);
            return (first < second ? -1 : first > second ? 1 : 0) * (sortOrder === "asc" ? 1 : -1);
        });
    }, [schedules, sortColumn, sortOrder]);
    const sort = (column: SortColumn) => {
        setHasAppliedSort(true);
        setSortOrder(previous => sortColumn === column && previous === "asc" ? "desc" : "asc");
        setSortColumn(column);
    };
    const reset = () => {
        setSortColumn("createdAt");
        setSortOrder("desc");
        setHasAppliedSort(false);
    };
    return (
        <>
            <div className="alarm-content--heading" aria-hidden={editorOpen}>
                <div>
                    <h3 className="heading heading__title--schedules">Schedule List</h3>
                    {hasAppliedSort && activeColumn && (
                        <SortSummary columnLabel={activeColumn.label}
                            sortOrder={sortOrder} onReset={reset} resetAriaLabel="Reset sorting to newest first" />
                    )}
                </div>
            </div>
            {loading ? <LoadingSpinner message="Loading schedules…" /> : (
                <div className="table-responsive">
                    <table className="table">
                        <thead><tr>
                            {COLUMNS.map(column => (
                                <th key={column.key} className={column.key === "description" ? "table-message th--sortable" : "th--sortable"}
                                    onClick={() => sort(column.key)}
                                    aria-sort={sortColumn === column.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}>
                                    {column.label} <SortIcon sortOrder={sortColumn === column.key ? sortOrder : null} />
                                </th>
                            ))}
                            <th>Actions</th>
                        </tr></thead>
                        <tbody>
                            {!sorted.length && <tr><td colSpan={5} style={{ textAlign: "center" }}>No schedules found.</td></tr>}
                            {sorted.map(schedule => (
                                <tr key={schedule.id}>
                                    <td>{schedule.name}</td>
                                    <td>{schedule.description || ""}</td>
                                    <td>{schedule.timeSlotCount ?? 0}</td>
                                    <td>{formatDateTime(schedule.createdAt)}</td>
                                    <td><ActionList disabled={busy} onEdit={() => onEdit(schedule)} onDelete={() => onDelete(schedule)} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
